import React, { useRef, useLayoutEffect } from 'react';
import styled from '@emotion/styled/macro';
import { css } from '@emotion/react';
import MaxWidthContainer from 'components/MaxWidthContainer';
import LayoutLogo from 'containers/LayoutLogo';
import PropTypes from 'prop-types';
import history from 'providers/history';
import NavLink from 'components/NavLink';
import CloseIcon from '@mui/icons-material/Close';
import IconButton from '@mui/material/IconButton';
import ArrowBackIosRoundedIcon from '@mui/icons-material/ArrowBackIosRounded';
import UserIcon from 'components/Icons/User';
import NotificationsIconButton from 'containers/NotificationsIconButton';
import useMediaQuery from '@mui/material/useMediaQuery';
import Grid from 'components/Grid';
import ChatIconButton from 'containers/Side/Chat/ChatIconButton';
import useFeature from 'hooks/useFeature';

const Wrapper = styled('div')`
  width: 100%;
  min-height: calc(var(--navbar-height) + var(--safe-area-inset-top));
  background-color: #fff;
  box-shadow: 0 1px 4px 0 rgba(0,0,0,0.05),
              0 2px 6px 0 rgba(0,0,0,0.05);

  position: fixed;
  top: 0;
  left: 0;
  right: 0;

  z-index: ${({ theme }) => theme.zIndex.navbar};

  padding-top: var(--safe-area-inset-top);

  display: flex;
  align-items: flex-end;
  padding-bottom: 4px;
`;

const LogoLink = styled(NavLink)`
  display: flex;
  align-items: center;
  min-width: 0;
`;

const NavbarLogo = styled(LayoutLogo)`
  max-width: 20vw;
  max-height: 26px;
`;

const Side = styled('div', { shouldForwardProp: prop => prop !== 'hideBelow' })`
  flex: none;
  display: flex;
  align-items: center;

  &:empty {
    display: none;
  }

  ${({ hideBelow }) => hideBelow && css`
    @media (max-width: ${hideBelow - 0.05}px) {
      display: none;
    }
  `}
`;

const Actions = styled(Side)`
  margin-left: auto;
`;

const Title = styled('div')`
  flex: 1 1 auto;
  min-width: 0;
  pointer-events: none;

  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;

  font-weight: 700;
  font-size: 1.2rem;

  ${({ theme }) => theme.breakpoints.down('sm')} {
    white-space: normal;
    display: -webkit-box;
    -webkit-box-orient: vertical;
    -webkit-line-clamp: 2;
    overflow-wrap: anywhere;
    line-height: 1.15;
    font-size: 1rem;
  }

  @media(max-width: 375px) { font-size: 0.95rem; }
`;

const InnerContainer = styled('div', { shouldForwardProp: prop => prop !== 'centerFrom' })`
  display: flex;
  align-items: center;
  gap: 0.5rem;

  ${({ centerFrom }) => centerFrom && css`
    @media (min-width: ${centerFrom}px) {
      & > ${Side}, & > ${Actions} {
        flex: 1 1 0;
      }

      & > ${Title} {
        flex: 0 1 auto;
        text-align: center;
      }
    }
  `}
`;

const MobileNavbar = ({ left = 'logo', leftTo, title, right = 'actions' }) => {

  const navbar = useRef();

  const smallScreen = useMediaQuery('(max-width: 360px)');

  const chatFeature = useFeature('chat');

  const hasLogoAndTitle = left === 'logo' && Boolean(title);

  const minWidthForLogoAndTitle = chatFeature.enabled ? 480 : 360;

  const logoWithTitleFrom = hasLogoAndTitle ? minWidthForLogoAndTitle : undefined;

  const leftValues = {
    logo: <LogoLink to="/"><NavbarLogo /></LogoLink>,
    back: (
      <IconButton edge="start" color="inherit" onClick={() => leftTo ? history.push(leftTo) : history.go(-1)}>
        <ArrowBackIosRoundedIcon />
      </IconButton>
    ),
    close: (
      <IconButton edge="start" color="inherit" onClick={() => leftTo ? history.push(leftTo) : history.go(-1)}>
        <CloseIcon />
      </IconButton>
    ),
  };

  const rightValues = {
    actions: (
      <Grid container spacing={0.5} wrap="nowrap" justifyContent="flex-end">
        {chatFeature.enabled && (
          <Grid item>
            <ChatIconButton color="inherit" size={smallScreen ? 'small' : 'medium'} />
          </Grid>
        )}
        <Grid item>
          <NotificationsIconButton color="inherit" component={NavLink} to="/notifications" size={smallScreen ? 'small' : 'medium'} />
        </Grid>
        <Grid item>
          <IconButton color="inherit" onClick={() => history.push('/menu')} size={smallScreen ? 'small' : 'medium'}>
            <UserIcon />
          </IconButton>
        </Grid>
      </Grid>
    ),
  };

  useLayoutEffect(() => {
    const preventDefault = e => {
      e.preventDefault();
    };

    const currentNavbar = navbar.current;

    currentNavbar.addEventListener('touchmove', preventDefault, { passive: false });

    return () => {
      currentNavbar.removeEventListener('touchmove', preventDefault, { passive: false });
    };
  }, []);

  return (
    <Wrapper ref={navbar}>
      {/* Chrome spans the frame, so it tracks the widest content column rather than the current page's */}
      <MaxWidthContainer widthVariant="wide">
        <InnerContainer centerFrom={logoWithTitleFrom}>
          <Side hideBelow={logoWithTitleFrom}>{leftValues[left]}</Side>
          {title && <Title>{title}</Title>}
          <Actions>{rightValues[right]}</Actions>
        </InnerContainer>
      </MaxWidthContainer>
    </Wrapper>
  );
};

MobileNavbar.propTypes = {
  left: PropTypes.oneOf(['logo', 'back', 'close']),
  leftTo: PropTypes.string,
  title: PropTypes.string,
  right: PropTypes.oneOf(['actions', null]),
};

export default MobileNavbar;
